/**
 * Book Performance Widget
 * Shows total book performance vs benchmark
 * Goal 2: Performance Analytics
 */

import React from 'react';
import { TrendingUp, TrendingDown, Target, Minus } from 'lucide-react';

interface BookPerformanceWidgetProps {
  bookPerformanceYTD: number | null;
  bookPerformanceMTD: number | null;
  benchmarkYTD: number | null;
  alpha: number | null;
  benchmarkName?: string;
}

const BookPerformanceWidget: React.FC<BookPerformanceWidgetProps> = ({
  bookPerformanceYTD,
  bookPerformanceMTD,
  benchmarkYTD,
  alpha,
  benchmarkName = 'SPI Index',
}) => {
  const formatPercent = (value: number | null | undefined) => {
    if (value === null || value === undefined) return 'N/A';
    const sign = value >= 0 ? '+' : '';
    return `${sign}${value.toFixed(2)}%`;
  };

  const hasPerformanceData = alpha !== null;
  const isOutperforming = hasPerformanceData && alpha > 0;
  // TASK-872: exactly-in-line is neither. It used to fall into the underperforming branch and
  // be shown in red as "behind by 0.00%".
  const isInLine = hasPerformanceData && alpha === 0;

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">Book Performance</h3>
        <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
          !hasPerformanceData ? 'bg-gray-100 text-gray-500' :
          isInLine ? 'bg-gray-100 text-gray-700' :
          isOutperforming ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
        }`}>
          {!hasPerformanceData ? (
            <TrendingDown className="w-4 h-4 mr-1 opacity-40" />
          ) : isInLine ? (
            <Minus className="w-4 h-4 mr-1" />
          ) : isOutperforming ? (
            <TrendingUp className="w-4 h-4 mr-1" />
          ) : (
            <TrendingDown className="w-4 h-4 mr-1" />
          )}
          Alpha: {formatPercent(alpha)}
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4">
        {/* YTD Performance */}
        <div className="text-center p-4 bg-blue-50 rounded-lg">
          <div className="text-xs text-gray-600 uppercase mb-1">YTD</div>
          <div className={`text-2xl font-bold ${
            bookPerformanceYTD === null ? 'text-gray-400' :
            bookPerformanceYTD >= 0 ? 'text-green-600' : 'text-red-600'
          }`}>
            {formatPercent(bookPerformanceYTD)}
          </div>
          <div className="text-xs text-gray-500 mt-1">Total Book</div>
        </div>

        {/* MTD Performance */}
        <div className="text-center p-4 bg-purple-50 rounded-lg">
          <div className="text-xs text-gray-600 uppercase mb-1">MTD</div>
          <div className={`text-2xl font-bold ${
            bookPerformanceMTD === null ? 'text-gray-400' :
            bookPerformanceMTD >= 0 ? 'text-green-600' : 'text-red-600'
          }`}>
            {formatPercent(bookPerformanceMTD)}
          </div>
          <div className="text-xs text-gray-500 mt-1">This Month</div>
        </div>

        {/* Benchmark */}
        <div className="text-center p-4 bg-gray-50 rounded-lg">
          <div className="text-xs text-gray-600 uppercase mb-1">Benchmark</div>
          <div className="text-2xl font-bold text-gray-700">
            {formatPercent(benchmarkYTD)}
          </div>
          <div className="text-xs text-gray-500 mt-1 flex items-center justify-center gap-1">
            <Target className="w-3 h-3" />
            {benchmarkName}
          </div>
        </div>
      </div>

      {/* Performance Insight - only show when data is available */}
      {hasPerformanceData ? (
        <div className={`mt-4 p-3 rounded-lg ${
          isInLine ? 'bg-gray-50 border border-gray-200' :
          isOutperforming ? 'bg-green-50 border border-green-200' :
          'bg-yellow-50 border border-yellow-200'
        }`}>
          <p className={`text-sm ${
            isInLine ? 'text-gray-700' : isOutperforming ? 'text-green-800' : 'text-yellow-800'
          }`}>
            {isInLine ? (
              /* TASK-872 review: `isOutperforming` is `alpha > 0`, so a book exactly in line
                 with its benchmark fell into the "Underperforming" branch and was told it was
                 behind by 0.00%, in a yellow warning box with a red down-arrow.
                 `PerformanceSummaryCards` already models this third case; this widget did
                 not, and the hunk that fixed the sign walked past it. */
              <><strong>In line with the benchmark.</strong> No material alpha either way.</>
            ) : isOutperforming ? (
              <>
                <strong>Outperforming benchmark by {formatPercent(alpha)}.</strong> Your book is generating positive alpha for clients.
              </>
            ) : (
              <>
                {/* TASK-872: `formatPercent` prefixes a `+` to any non-negative number, and
                    `Math.abs` had just made this one non-negative — so a shortfall read
                    "Underperforming benchmark by +8.58%". The magnitude is right; the sign
                    contradicted the sentence around it. */}
                <strong>
                  Underperforming benchmark by {formatPercent(Math.abs(alpha!)).replace('+', '')}.
                </strong>{' '}
                Consider reviewing positioning strategies.
              </>
            )}
          </p>
        </div>
      ) : (
        <div className="mt-4 p-3 rounded-lg bg-gray-50 border border-gray-200">
          <p className="text-sm text-gray-500">
            Performance metrics are not yet available. Data will appear once performance calculations are processed.
          </p>
        </div>
      )}
    </div>
  );
};

export default BookPerformanceWidget;
